import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Chart from 'react-apexcharts';
import { useNavigate } from 'react-router-dom';
import Loader from '../../../../component/common/Loader';
import { formatBigNumber, lastNDaysLabels } from '../../../SleepHub/sleepHubApi';
import { getLocalStorageItem } from '../../../../utils/helper';
import { fetchCompanyCessationOverview } from './companyCessationApi';
import SuppressedPanel from './SuppressedPanel';
import {
  StatCard,
  Panel,
  PanelEmpty,
  PanelTable,
  MiniDonutCard,
  PANEL_ROW_CLASS,
  STAT_ICONS,
  CHART_BASE,
  CHART_COLORS,
  CHART_GRID
} from '../../../../component/common/designSystem';
import { PROGRAMME_COLORS, PROGRAMME_PATHS, OVERLAP_RAMP, RUN_RAMP } from './cessationTheme';

/**
 * Cessation → Overview.
 *
 * Three things carry the page:
 *
 *  1. HOW MANY PEOPLE ARE TRYING TO STOP SOMETHING. Counted across both
 *     programmes at once and de-duplicated, so somebody quitting smoking and
 *     drinking is one person having a hard year, not two features being used.
 *  2. THE OVERLAP. It gets its own figure because it is the group under the
 *     most strain, and because neither programme's own page can show it — each
 *     counts those people without ever revealing they are the same people.
 *  3. WHETHER IT IS WORKING. Not "did they succeed" — nobody quits on a
 *     dashboard's schedule — but how many are currently past a week, how many
 *     logged days passed without a slip, and whether people are still turning
 *     up. A programme with high enrolment and no logging is a programme people
 *     signed up to and abandoned, and it looks identical to a healthy one if
 *     you only count enrolments.
 *
 * Nobody is named on this page or reachable from it, and a scope with too few
 * participants renders the withheld state instead of numbers.
 *
 * `fetcher` is a prop so an unscoped hub could render this page unchanged.
 */
const CessationOverview = ({
  fetcher = fetchCompanyCessationOverview,
  populationLabel = 'users'
}) => {
  const navigate = useNavigate();
  const userData = JSON.parse(getLocalStorageItem('userData') || '{}');
  const slug = userData?.slug;

  const [loader, setLoader] = useState(true);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    let mounted = true;
    setLoader(true);
    fetcher().then((data) => {
      if (!mounted) return;
      setStats(data);
      setLoader(false);
    });
    return () => {
      mounted = false;
    };
  }, [fetcher]);

  if (loader) return <Loader />;

  // The whole page is withheld, not just a panel: with a cohort this small
  // every figure on it would be about identifiable people.
  if (stats?.suppressed) {
    return <SuppressedPanel minimum={stats.minimum} what="Last Puff or Last Drink" />;
  }

  const programmes = stats?.programmes || [];
  const reported = programmes.filter((row) => !row.suppressed);
  const withheld = programmes.filter((row) => row.suppressed);

  const trendLength = reported[0]?.trend?.length || 0;
  const trendHasData = reported.some((row) => (row.trend || []).some((day) => day.logs > 0));

  const trendOptions = {
    chart: { ...CHART_BASE, type: 'line' },
    colors: reported.map((row) => PROGRAMME_COLORS[row.key] || CHART_COLORS.gray),
    stroke: { width: 3, curve: 'smooth' },
    dataLabels: { enabled: false },
    grid: CHART_GRID,
    legend: { position: 'top', horizontalAlign: 'right' },
    xaxis: { categories: lastNDaysLabels(trendLength), tickAmount: 6 },
    yaxis: { labels: { formatter: (val) => formatBigNumber(val) } },
    tooltip: { shared: true }
  };

  /**
   * Take-up per programme as one horizontal bar. Both bars share the SAME
   * denominator — the whole population — so they are directly comparable, which
   * two separate donuts could never be.
   */
  const adoptionOptions = {
    chart: { ...CHART_BASE, type: 'bar' },
    colors: [({ dataPointIndex }) => PROGRAMME_COLORS[reported[dataPointIndex]?.key]],
    plotOptions: {
      bar: { horizontal: true, borderRadius: 3, barHeight: '45%', distributed: true }
    },
    dataLabels: { enabled: false },
    legend: { show: false },
    grid: CHART_GRID,
    xaxis: {
      categories: reported.map((row) => row.label),
      labels: { formatter: (val) => `${Math.round(val)}%` },
      max: 100
    },
    tooltip: {
      y: {
        formatter: (val, { dataPointIndex }) =>
          `${val}% — ${formatBigNumber(reported[dataPointIndex]?.participants || 0)} people`
      }
    }
  };

  return (
    <div>
      {/* ---------- Headline ---------- */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6 gap-4">
        <StatCard
          icon={STAT_ICONS.users}
          title="People Quitting"
          value={formatBigNumber(stats?.participants || 0)}
        />
        <StatCard
          icon={STAT_ICONS.activeUsers}
          title={`Share of ${populationLabel}`}
          value={stats?.totalUsers > 0 ? `${stats.adoption}%` : '-'}
        />
        {/* The group under the most strain, and the one figure neither
            programme page can show without linking the two together. */}
        <StatCard
          icon={STAT_ICONS.personalBest}
          title="Quitting Both"
          value={formatBigNumber(stats?.bothProgrammes || 0)}
        />
        <StatCard
          icon={STAT_ICONS.streak}
          title="A Week or More Clear"
          value={formatBigNumber(stats?.slipFreeWeek || 0)}
        />
        <StatCard
          icon={STAT_ICONS.onlineNow}
          title="Logging Recently"
          value={formatBigNumber(stats?.activeUsers || 0)}
        />
        <StatCard
          icon={STAT_ICONS.logs}
          title="Check-ins Logged"
          value={formatBigNumber(stats?.logs || 0)}
        />
      </div>

      {/* ---------- Shape of it ---------- */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-6">
        <MiniDonutCard
          title="Which Programme"
          subtitle="People on one, the other, or both"
          data={stats?.overlapSplit || []}
          colors={OVERLAP_RAMP}
          unit="people"
          totalLabel="People"
          legendLimit={3}
        />
        <Panel
          title="Take-up by Programme"
          subtitle={`Share of your ${populationLabel} on each one`}
        >
          {reported.length > 0 && stats?.totalUsers > 0 ? (
            <Chart
              type="bar"
              height={260}
              width="100%"
              series={[{ name: 'Take-up', data: reported.map((row) => row.adoption) }]}
              options={adoptionOptions}
            />
          ) : (
            <PanelEmpty>Nothing to report at this group size.</PanelEmpty>
          )}
        </Panel>
        <Panel title="Logging Over Time" subtitle="Check-ins and cravings — last 30 days">
          {trendHasData ? (
            <Chart
              type="line"
              height={260}
              width="100%"
              series={reported.map((row) => ({
                name: row.label,
                data: (row.trend || []).map((day) => day.logs || 0)
              }))}
              options={trendOptions}
            />
          ) : (
            <PanelEmpty>Nothing logged in the last 30 days.</PanelEmpty>
          )}
        </Panel>
      </div>

      {/* ---------- How each is going ---------- */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mt-6">
        {reported.map((row) => (
          <MiniDonutCard
            key={row.key}
            title={`${row.label} — Current Runs`}
            subtitle="How long since each person last slipped"
            data={row.runSplit || []}
            colors={RUN_RAMP}
            unit="people"
            totalLabel="People"
            legendLimit={6}
          />
        ))}
      </div>

      {/* ---------- Side by side ---------- */}
      <div className="mt-6">
        <Panel
          title="Programme by Programme"
          subtitle="Reach, staying power, and how the attempts are going"
        >
          {reported.length > 0 ? (
            <PanelTable
              head={
                <>
                  <th className="py-2 pr-4 font-medium">Programme</th>
                  <th className="py-2 px-4 font-medium text-center">People</th>
                  <th className="py-2 px-4 font-medium text-center">Take-up</th>
                  <th className="py-2 px-4 font-medium text-center">Logging</th>
                  <th className="py-2 px-4 font-medium text-center">Week+ clear</th>
                  <th className="py-2 px-4 font-medium text-center">Clear days</th>
                  <th className="py-2 font-medium text-right">Cravings resisted</th>
                </>
              }
            >
              {reported.map((row) => (
                <tr
                  key={row.key}
                  className={`${PANEL_ROW_CLASS} cursor-pointer`}
                  onClick={() => navigate(`/${slug}/cessation/${PROGRAMME_PATHS[row.key]}`)}
                >
                  <td className="py-2 pr-4">
                    <span className="inline-flex items-center gap-2">
                      <span
                        className="w-2.5 h-2.5 rounded-full shrink-0"
                        style={{ backgroundColor: PROGRAMME_COLORS[row.key] }}
                      />
                      {row.label}
                    </span>
                  </td>
                  <td className="py-2 px-4 text-center">{formatBigNumber(row.participants)}</td>
                  <td className="py-2 px-4 text-center">{row.adoption}%</td>
                  <td className="py-2 px-4 text-center">{formatBigNumber(row.activeUsers)}</td>
                  <td className="py-2 px-4 text-center">{formatBigNumber(row.slipFreeWeek)}</td>
                  {/* Share of all logged days that passed without a slip. The
                      fairest single measure either programme has: it counts
                      days people got through, not whether they have finished. */}
                  <td className="py-2 px-4 text-center">{row.slipFreeDayRate}%</td>
                  <td className="py-2 text-right">
                    {row.cravingResistRate}%
                    <span className="ml-1.5 text-gray-400 font-normal">of cravings</span>
                  </td>
                </tr>
              ))}
            </PanelTable>
          ) : (
            <PanelEmpty>Neither programme has enough participants to report.</PanelEmpty>
          )}
        </Panel>
      </div>

      {/* A programme can be withheld on its own while the combined total is
          reportable — say so, rather than letting it look like nobody uses it. */}
      {withheld.length > 0 ? (
        <p className="mt-3 text-xs text-gray-400 dark:text-gray-500">
          {withheld.map((row) => row.label).join(' and ')} {withheld.length === 1 ? 'is' : 'are'}{' '}
          not shown separately: fewer than {stats?.minimum} people in this view{' '}
          {withheld.length === 1 ? 'is' : 'are'} using {withheld.length === 1 ? 'it' : 'them'}, so
          any figure would be about identifiable individuals.
        </p>
      ) : null}

      <p className="mt-3 text-xs text-gray-400 dark:text-gray-500">
        No individual is named in this section, and there is no way to reach one from it. Being
        enrolled in a quit programme is health information about a person, and it belongs to them.
      </p>
    </div>
  );
};

CessationOverview.propTypes = {
  fetcher: PropTypes.func,
  populationLabel: PropTypes.string
};

export default CessationOverview;
